<template>
  <div class="account-menu" @keydown.esc="closeAll">
    <slot :toggle="toggleMenu" :open="menuOpen"></slot>

    <div
      v-if="menuOpen"
      class="account-menu__panel"
      role="menu"
      aria-label="Account"
    >
      <div class="account-menu__identity">
        <strong>{{ accountName }}</strong>
        <span>{{ roleLabel }}</span>
      </div>

      <button type="button" class="account-menu__item" role="menuitem" @click="openProfile">
        <span class="account-menu__icon account-menu__icon--profile" aria-hidden="true">
          <i class="fas fa-user"></i>
        </span>
        <span>Profile</span>
      </button>

      <button type="button" class="account-menu__item" role="menuitem" @click="openPassword">
        <span class="account-menu__icon account-menu__icon--password" aria-hidden="true">
          <i class="fas fa-cog"></i>
        </span>
        <span>Change Password</span>
      </button>

      <div class="account-menu__logout-row">
        <button type="button" class="account-menu__logout" role="menuitem" @click="logout">
          <span>Log out</span>
          <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <div
      v-if="profileOpen"
      class="account-menu__dialog"
      role="presentation"
      @click.self="profileOpen = false"
    >
      <div class="account-menu__dialog-card" role="dialog" aria-labelledby="account-profile-title">
        <h3 id="account-profile-title">Profile</h3>
        <dl>
          <div>
            <dt>Name</dt>
            <dd>{{ accountName }}</dd>
          </div>
          <div v-if="account.username">
            <dt>Username</dt>
            <dd>{{ account.username }}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>{{ roleLabel }}</dd>
          </div>
          <div v-if="account.email">
            <dt>Email</dt>
            <dd>{{ account.email }}</dd>
          </div>
        </dl>
        <button type="button" class="account-menu__dialog-close" @click="profileOpen = false">
          Close
        </button>
      </div>
    </div>

    <div
      v-if="passwordOpen"
      class="account-menu__dialog"
      role="presentation"
      @click.self="passwordOpen = false"
    >
      <form
        class="account-menu__dialog-card"
        role="dialog"
        aria-labelledby="account-password-title"
        @submit.prevent="submitPassword"
      >
        <h3 id="account-password-title">Change Password</h3>
        <label class="account-menu__field">
          <span>Current password</span>
          <input
            v-model="password.current_password"
            class="account-menu__control"
            type="password"
            autocomplete="current-password"
            placeholder="Current password"
            required
          />
        </label>
        <label class="account-menu__field">
          <span>New password</span>
          <input
            v-model="password.new_password"
            class="account-menu__control"
            type="password"
            autocomplete="new-password"
            placeholder="New password"
            required
          />
        </label>
        <label class="account-menu__field">
          <span>Confirm password</span>
          <input
            v-model="password.confirm_password"
            class="account-menu__control"
            type="password"
            autocomplete="new-password"
            placeholder="Confirm password"
            required
          />
        </label>
        <p v-if="passwordError" class="account-menu__error" role="alert">{{ passwordError }}</p>
        <div class="account-menu__dialog-actions">
          <button type="button" class="account-menu__dialog-close" @click="passwordOpen = false">
            Cancel
          </button>
          <button type="submit" class="account-menu__dialog-save" :disabled="passwordSaving">
            {{ passwordSaving ? "Saving…" : "Change" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import { mapState } from "vuex";
import { USER_TYPES } from "@/utils/permissions";

export default {
  name: "AccountMenu",
  props: {
    name: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      menuOpen: false,
      profileOpen: false,
      passwordOpen: false,
      account: {},
      password: {
        current_password: "",
        new_password: "",
        confirm_password: "",
      },
      passwordError: "",
      passwordSuccess: "",
      passwordSaving: false,
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    accountName() {
      return this.account.username || this.name || "Account";
    },
    roleLabel() {
      if (this.account.role) return this.account.role;
      const type = Number(
        this.account.usertype != null
          ? this.account.usertype
          : this.user && this.user.usertype,
      );
      if (type === USER_TYPES.KITCHEN) return "Kitchen";
      if (type === USER_TYPES.WAITER) return "Waiter";
      return "Admin";
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocumentClick);
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      if (this.menuOpen) this.loadAccount();
    },
    closeAll() {
      this.menuOpen = false;
      this.profileOpen = false;
      this.passwordOpen = false;
    },
    onDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.menuOpen = false;
      }
    },
    async loadAccount() {
      try {
        const res = await axios.get("auth/user");
        this.account = res.data || {};
      } catch (e) {
        this.account = {};
      }
    },
    openProfile() {
      this.menuOpen = false;
      this.profileOpen = true;
      if (!this.account.username) this.loadAccount();
    },
    openPassword() {
      this.menuOpen = false;
      this.passwordOpen = true;
      this.passwordError = "";
      this.passwordSuccess = "";
      this.password = {
        current_password: "",
        new_password: "",
        confirm_password: "",
      };
    },
    logout() {
      this.closeAll();
      this.$emit("logout");
    },
    async submitPassword() {
      this.passwordError = "";
      this.passwordSuccess = "";
      const next = (this.password.new_password || "").trim();
      const confirm = (this.password.confirm_password || "").trim();
      if (!this.password.current_password || !next || !confirm) {
        this.passwordError = "Enter your current password and a new password.";
        return;
      }
      if (next !== confirm) {
        this.passwordError = "New password and confirmation do not match.";
        return;
      }
      if (next.length < 3 || next.length > 30 || /\s/.test(next)) {
        this.passwordError = "New password must be 3 to 30 characters with no spaces.";
        return;
      }
      this.passwordSaving = true;
      try {
        await axios.post("auth/changes-password", {
          current_password: this.password.current_password,
          new_password: next,
          confirm_password: confirm,
        });
        this.passwordOpen = false;
        this.password = {
          current_password: "",
          new_password: "",
          confirm_password: "",
        };
        this.$fire({
          title: "Password updated",
          text: "Your password was changed.",
          type: "success",
          timer: 2000,
        });
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.passwordError =
          (data && (data.message || data.error)) ||
          (typeof data === "string" && data) ||
          "Could not change password.";
      } finally {
        this.passwordSaving = false;
      }
    },
  },
};
</script>

<style>
.account-menu {
  position: relative;
}

.account-menu__panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 40;
  width: 280px;
  padding: 8px 0 6px;
  background: #ffffff !important;
  color: #1e293b !important;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
}

.account-menu__identity {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px 10px;
  border-bottom: 1px solid #e2e8f0;
}

.account-menu__identity strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a !important;
}

.account-menu__identity span {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: #64748b !important;
}

.account-menu__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: #1e293b !important;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
}

.account-menu__item:hover {
  background: #f8fafc;
}

.account-menu__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #ffffff !important;
  font-size: 13px;
}

.account-menu__icon--profile {
  background: #26c6da;
}

.account-menu__icon--password {
  background: #43a047;
}

.account-menu__logout-row {
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px 6px;
  border-top: 1px solid #e2e8f0;
}

.account-menu__logout {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #1976d2;
  border-radius: 16px;
  background: #ffffff !important;
  color: #1976d2 !important;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.account-menu__logout:hover {
  background: #e3f2fd !important;
}

.account-menu__dialog {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}

.account-menu__dialog-card {
  width: 100%;
  max-width: 320px;
  padding: 16px;
  background: #ffffff !important;
  color: #0f172a !important;
  border-radius: 12px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  text-align: left;
}

.account-menu__dialog-card h3 {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a !important;
  text-align: center;
}

.account-menu__dialog-card dl {
  margin: 0;
}

.account-menu__dialog-card dl div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}

.account-menu__dialog-card dt {
  color: #64748b !important;
  font-size: 13px;
}

.account-menu__dialog-card dd {
  margin: 0;
  color: #0f172a !important;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
}

.account-menu__field {
  display: flex !important;
  flex-direction: column !important;
  align-items: stretch !important;
  gap: 4px;
  margin: 0 0 10px;
  text-align: left !important;
  color: #334155 !important;
  font-size: 12px !important;
  font-weight: 600;
  line-height: 1.2;
}

.account-menu__control::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
}

html.kot-dark .account-menu__control::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
}

.account-menu__control {
  display: block !important;
  width: 100% !important;
  height: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  margin: 0 !important;
  padding: 4px 10px !important;
  box-sizing: border-box !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 22px !important;
}

.account-menu__error {
  margin: 0 0 10px;
  color: #b91c1c !important;
  font-size: 13px;
}

.account-menu__success {
  margin: 0 0 10px;
  color: #166534 !important;
  font-size: 13px;
}

.account-menu__dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.account-menu__dialog-close,
.account-menu__dialog-save {
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.account-menu__dialog-close {
  border: 1px solid #cbd5e1;
  background: #ffffff !important;
  color: #475569 !important;
}

.account-menu__dialog-save {
  border: 1px solid #1976d2;
  background: #1976d2 !important;
  color: #ffffff !important;
}

.account-menu__dialog-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

html.kot-dark .account-menu__panel,
html.kot-dark .account-menu__panel button,
html.kot-dark .account-menu__item,
html.kot-dark .account-menu__identity strong,
html.kot-dark .account-menu__dialog-card,
html.kot-dark .account-menu__dialog-card h3,
html.kot-dark .account-menu__dialog-card p,
html.kot-dark .account-menu__dialog-card label,
html.kot-dark .account-menu__dialog-card dt,
html.kot-dark .account-menu__dialog-card dd {
  background: #ffffff;
  color: #1e293b !important;
}

html.kot-dark .account-menu__panel .account-menu__item,
html.kot-dark .account-menu__panel .account-menu__item > span:not(.account-menu__icon) {
  background: transparent !important;
  color: #1e293b !important;
}

html.kot-dark .account-menu__panel .account-menu__identity span,
html.kot-dark .account-menu__dialog-card dt {
  background: transparent !important;
  color: #64748b !important;
}

html.kot-dark .account-menu__panel span.account-menu__icon--profile {
  background: #26c6da !important;
  color: #ffffff !important;
}

html.kot-dark .account-menu__panel span.account-menu__icon--password {
  background: #43a047 !important;
  color: #ffffff !important;
}

html.kot-dark .account-menu__panel .account-menu__icon i {
  color: #ffffff !important;
  background: transparent !important;
}

html.kot-dark .account-menu__panel .account-menu__logout,
html.kot-dark .account-menu__panel .account-menu__logout span,
html.kot-dark .account-menu__panel .account-menu__logout i {
  background: #ffffff !important;
  color: #1976d2 !important;
}

html.kot-dark .account-menu__control {
  background: #ffffff !important;
  color: #0f172a !important;
  height: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
}
</style>
